import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Signal,
  signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  formControlChanges$,
  NgCommonModule,
  NgDialogModule,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  IMarketingCampaignEntry,
  IMarketingCampaignRun,
  MarketingCampaignRunStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  firstValueFrom,
  shareReplayCold,
  WithRef,
} from '@principle-theorem/shared';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CampaignRunEntryManager,
  IMarketingCampaignRunEntryStats,
} from '../campaign-run-entry-manager';

export interface IApproveEntriesDialogRequest {
  campaignRun: WithRef<IMarketingCampaignRun>;
  entries: WithRef<IMarketingCampaignEntry>[];
}

export interface IApproveEntriesDialogResponse {
  approvedEntries: WithRef<IMarketingCampaignEntry>[];
}

export interface IApproveEntriesFormGroup {
  skipSharedContactDetails: boolean;
}

@Component({
  selector: 'pr-approve-entries-dialog',
  templateUrl: './approve-entries-dialog.component.html',
  styleUrl: './approve-entries-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    NgDialogModule,
    NgCommonModule,
    MatCheckboxModule,
  ],
})
export class ApproveEntriesDialogComponent {
  private _dialogRef = inject(
    MatDialogRef<ApproveEntriesDialogComponent, IApproveEntriesDialogResponse>
  );
  data = inject<IApproveEntriesDialogRequest>(MAT_DIALOG_DATA);
  skipSharedContactDetails = new TypedFormControl<boolean>(true);

  title = signal<string>('Start Campaign Run');
  submitLabel = signal<string>('Start Campaign Run');
  sharedContactDetailsCount: Signal<number>;
  entryManager: CampaignRunEntryManager;

  approvingEntries$: Observable<WithRef<IMarketingCampaignEntry>[]>;
  approvingEntriesCount$: Observable<number>;

  constructor() {
    this.entryManager = new CampaignRunEntryManager(of(this.data.campaignRun));

    const skipSharedContactDetails$ = formControlChanges$(
      this.skipSharedContactDetails
    ).pipe(map((skip) => skip ?? true));

    const withSharedContactDetails = this.data.entries;
    const withoutSharedContactDetails = withSharedContactDetails.filter(
      (entry) => !entry.hasDuplicateEmail && !entry.hasDuplicateMobile
    );

    this.approvingEntries$ = combineLatest([
      this.entryManager.stats$,
      skipSharedContactDetails$,
    ]).pipe(
      map(([stats, skipSharedContactDetails]) => {
        const possibleEntries = skipSharedContactDetails
          ? withoutSharedContactDetails
          : withSharedContactDetails;
        return this._limitEntries(possibleEntries, stats);
      }),
      shareReplayCold()
    );
    this.approvingEntriesCount$ = this.approvingEntries$.pipe(
      map((entries) => entries.length)
    );

    const sharedContactDetails = this.data.entries.filter(
      (entry) => entry.hasDuplicateEmail || entry.hasDuplicateMobile
    );
    this.sharedContactDetailsCount = signal(sharedContactDetails.length);

    if (this.data.campaignRun.status !== MarketingCampaignRunStatus.Draft) {
      this.title.set('Approve Entries');
      this.submitLabel.set('Approve Entries');
    }
  }

  async submit(): Promise<void> {
    const approvedEntries = await firstValueFrom(this.approvingEntries$);
    if (!approvedEntries) {
      return;
    }
    this._dialogRef.close({ approvedEntries });
  }

  private _limitEntries(
    entries: WithRef<IMarketingCampaignEntry>[],
    stats?: IMarketingCampaignRunEntryStats
  ): WithRef<IMarketingCampaignEntry>[] {
    if (!stats || stats.remainingEntryCount <= 0) {
      return [];
    }
    return entries.slice(0, stats.remainingEntryCount);
  }
}
